<template>
  <div v-if="billingDetails?.paymentMethods && billingDetails?.paymentMethods.length > 0" class="container-fluid d-flex h-100 flex-column">
    <div class="row g-0">
      <div class="col">
        <h5 class="current mb-1">
          {{ t("billing_summary.current_plan") }}: {{ planLabel }} at {{ currency(billingDetails?.planPrice || 0) }} /
          {{ t(`billing_summary.${billingDetails?.paymentInterval}`) }}
        </h5>
      </div>
    </div>

    <!-- Additional serialized codes (one-time) -->
    <div class="row g-0 py-2 align-items-center current">
      <div class="col">
        <p class="card-text item-title mb-0">{{ t("billing_add_ons.additional_codes_title") }}</p>
        <p class="card-text item-detail mb-0">{{ t("billing_add_ons.additional_codes_price", { price: currency(codeBlockPrice) }) }}</p>
      </div>
      <div class="col-auto d-flex justify-content-end">
        <div class="spinner form-control d-flex align-items-stretch" role="group" tabindex="-1">
          <button class="btn btn-sm border-0 rounded-0 py-0 spinner-button" tabindex="-1" type="button" @click="updateCodeBlocks('dec')">
            <i class="bi bi-dash"></i>
          </button>
          <output class="flex-grow-1 align-self-center" dir="ltr" role="spinbutton" tabindex="0"
            ><bdi>{{ codeBlocks }}</bdi></output
          >
          <button class="btn btn-sm border-0 rounded-0 py-0 spinner-button" tabindex="-1" type="button" @click="updateCodeBlocks('inc')">
            <i class="bi bi-plus"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Content localization (per language) -->
    <div class="row g-0 py-2 align-items-center current">
      <div class="col">
        <p class="card-text item-title mb-0">{{ t("billing_summary.add_on_types.localization") }}</p>
        <p v-if="localizationIncluded" class="card-text item-detail text-muted mb-0">{{ t("billing_add_ons.included") }}</p>
        <p v-else class="card-text item-detail mb-0">
          {{ t("billing_add_ons.per_language", { price: currency(localizationPrice * periodMultiplier), period: periodLabel }) }}
          <span v-if="scheduledLanguages < liveLanguages" class="text-muted">
            · {{ t("billing_summary.drops_to_on", { count: scheduledLanguages, date: renewalDateLabel }) }}
          </span>
        </p>
      </div>
      <div v-if="!localizationIncluded" class="col-auto d-flex justify-content-end">
        <div class="spinner form-control d-flex align-items-stretch" role="group" tabindex="-1">
          <button class="btn btn-sm border-0 rounded-0 py-0 spinner-button" tabindex="-1" type="button" @click="updateLanguages('dec')">
            <i class="bi bi-dash"></i>
          </button>
          <output class="flex-grow-1 align-self-center" dir="ltr" role="spinbutton" tabindex="0"
            ><bdi>{{ languages }}</bdi></output
          >
          <button class="btn btn-sm border-0 rounded-0 py-0 spinner-button" tabindex="-1" type="button" @click="updateLanguages('inc')">
            <i class="bi bi-plus"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Integrations (on/off) -->
    <div v-for="addOn in toggleAddOns" :key="addOn.type" class="row g-0 py-2 align-items-center current">
      <div class="col">
        <p class="card-text item-title mb-0">{{ t(`billing_summary.add_on_types.${addOn.type}`) }}</p>
        <p v-if="isIncluded(addOn)" class="card-text item-detail text-muted mb-0">{{ t("billing_add_ons.included") }}</p>
        <p v-else class="card-text item-detail mb-0">
          {{
            t("billing_add_ons.setup_and_recurring", { setup: currency(addOn.setup), price: currency(addOn.monthly * periodMultiplier), period: periodLabel })
          }}
          <span v-if="isPendingRemoval(addOn.type)" class="text-muted"> · {{ t("billing_summary.ends_on", { date: renewalDateLabel }) }}</span>
        </p>
      </div>
      <div class="col-auto">
        <div class="form-check form-switch mb-0">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            :checked="isIncluded(addOn) || selected[addOn.type]"
            :disabled="isIncluded(addOn)"
            @change="selected[addOn.type] = ($event.target as HTMLInputElement).checked"
          />
        </div>
      </div>
    </div>

    <!-- Totals -->
    <div class="row g-0 pt-2">
      <div class="col">
        <p class="card-text total-line mb-0">{{ t("billing_add_ons.due_today") }}</p>
        <p class="card-text total-line mb-0">{{ t("billing_add_ons.recurring_add_ons") }}</p>
      </div>
      <div class="col-auto text-end">
        <p class="card-text total-line mb-0">{{ previewing ? "…" : currency(dueToday) }}</p>
        <p class="card-text total-line mb-0">{{ currency(recurringTotal) }} / {{ periodLabel }}</p>
      </div>
    </div>

    <!-- Charge confirmation -->
    <div v-if="hasChanges" class="row g-0 pt-2">
      <div class="col">
        <p class="card-text charge-notice mb-0">
          <template v-if="previewing">{{ t("billing_add_ons.calculating") }}</template>
          <template v-else-if="dueToday > 0">{{ t("billing_add_ons.charge_notice", { card: cardLabel, amount: currency(dueToday) }) }}</template>
          <template v-else>{{ t("billing_add_ons.no_charge_notice", { date: renewalDateLabel }) }}</template>
        </p>
      </div>
    </div>

    <div class="row flex-grow-1 align-items-end text-end row-bottom pb-3 pe-0">
      <div class="col mt-3 align-right">
        <div class="btn-group" role="group">
          <button class="btn btn-outline-secondary btn-sm rounded-pill ms-2 action-button" type="button" @click="summary">
            {{ t("billing_summary.go_back") }}
          </button>
          <button
            ref="confirmButtonRef"
            class="btn btn-sm rounded-pill ms-2 action-button"
            :class="confirmButton"
            type="button"
            @click="confirmChanges"
            :disabled="disableSubmit || !hasChanges || previewing"
          >
            {{ t("billing_summary.confirm_changes") }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-else>
    <div class="row g-0">
      <div class="col">
        <h5 class="current mb-3">{{ t("billing_summary.current_plan_free") }}</h5>
        <p class="card-text">{{ t("billing_add_ons.paid_plans_only") }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Billing from "../../models/Billing";
  import Plan from "../../models/Plan";
  import { updateAddOns, previewAddOns } from "@/services/PaymentService";
  import { useToast } from "vue-toastification";
  import { useStore } from "vuex";
  import { computed, inject, onMounted, ref, watch } from "vue";
  import { useI18n } from "vue-i18n";

  const { t } = useI18n();

  const props = defineProps<{
    billingDetails?: Billing;
    unarchive?: boolean;
  }>();

  const $store = useStore();
  const toast = useToast();
  const emitter: any = inject("emitter");

  const confirmButtonRef = ref<HTMLButtonElement>();

  type ToggleAddOn = { type: string; setup: number; monthly: number };

  const confirmedButtonHTML = `<div class="confirmed-text">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-check-circle" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                  </svg> <span class="confirmed-text-span">Changes confirmed</span>
                </div>`;

  const codeBlocks = ref(0);
  const languages = ref(0);
  const selected = ref<Record<string, boolean>>({});
  const disableSubmit = ref(false);
  const confirmButton = ref("btn-primary");

  // Exact amount due today, from the backend preview
  const dueToday = ref(0);
  const previewing = ref(false);
  let previewTimer: ReturnType<typeof setTimeout> | undefined;

  const planLabel = computed((): string => {
    return props.billingDetails?.subscriptionName === "Pro" ? "Professional" : props.billingDetails?.subscriptionName || "";
  });

  // Current plan's pricing from the plans table (Basic -> basic, Pro -> pro, Enterprise -> enterprise)
  const plan = computed((): Plan => {
    const plans: Plan[] = $store.getters.plans || [];
    const name = props.billingDetails?.subscriptionName?.toLowerCase();
    return plans.find((p) => p.name === name) || {};
  });

  // Recurring add-ons follow the plan's billing interval: yearly plans pay 12x the monthly price
  const isYearly = computed((): boolean => props.billingDetails?.paymentInterval === "yearly");
  const periodMultiplier = computed((): number => (isYearly.value ? 12 : 1));
  const periodLabel = computed((): string => (isYearly.value ? t("billing_add_ons.period_year") : t("billing_add_ons.period_month")));

  const codeBlockPrice = computed((): number => plan.value.additionalCodesBlockPrice ?? 0);
  const localizationPrice = computed((): number => plan.value.localizationMonthlyPrice ?? 0);
  const localizationIncluded = computed((): boolean => localizationPrice.value === 0);

  const toggleAddOns = computed((): ToggleAddOn[] => [
    { type: "printer", setup: plan.value.printerIntegrationSetupPrice ?? 0, monthly: plan.value.printerIntegrationMonthlyPrice ?? 0 },
    { type: "pim_dam", setup: plan.value.pimDamSetupPrice ?? 0, monthly: plan.value.pimDamMonthlyPrice ?? 0 },
    { type: "epcis", setup: plan.value.epcisSetupPrice ?? 0, monthly: plan.value.epcisMonthlyPrice ?? 0 },
  ]);

  const defaultPayment = computed(() => {
    const methods = props.billingDetails?.paymentMethods || [];
    return methods.find((pm) => pm.default) || methods[0];
  });

  const cardLabel = computed((): string => {
    if (!defaultPayment.value) return "";
    const brand = defaultPayment.value.brand.charAt(0).toUpperCase() + defaultPayment.value.brand.slice(1);
    return `${brand} •••• ${defaultPayment.value.last_four}`;
  });

  const renewalDateLabel = computed((): string => formatLongDate(props.billingDetails?.renewalDate));

  function isIncluded(addOn: ToggleAddOn) {
    return addOn.setup === 0 && addOn.monthly === 0;
  }

  // On the subscription right now (may still be scheduled to end at renewal)
  function isLive(type: string) {
    return !!props.billingDetails?.addOns?.some((a) => a.type === type);
  }

  // Active now but scheduled to drop off at renewal
  function isPendingRemoval(type: string) {
    const scheduled = props.billingDetails?.scheduledAddOns;
    return isLive(type) && !!scheduled && !scheduled.some((s) => s.type === type);
  }

  // Where each integration stands going forward, before any changes on this screen
  function baselineOn(type: string) {
    return isLive(type) && !isPendingRemoval(type);
  }

  // Languages on the subscription now, and how many remain after renewal
  const liveLanguages = computed((): number => props.billingDetails?.addOns?.find((a) => a.type === "localization")?.quantity ?? 0);
  const scheduledLanguages = computed((): number => {
    const scheduled = props.billingDetails?.scheduledAddOns;
    if (!scheduled) return liveLanguages.value;
    return scheduled.find((s) => s.type === "localization")?.quantity ?? 0;
  });

  // Switched on here and not on the subscription yet: setup fee + prorated charge
  const newlyEnabled = computed((): ToggleAddOn[] => toggleAddOns.value.filter((a) => !isIncluded(a) && selected.value[a.type] && !isLive(a.type)));

  // Switched back on while a removal is pending: no charge, removal is cancelled
  const restored = computed((): ToggleAddOn[] => toggleAddOns.value.filter((a) => selected.value[a.type] && isPendingRemoval(a.type)));

  const disabled = computed((): ToggleAddOn[] => toggleAddOns.value.filter((a) => !isIncluded(a) && !selected.value[a.type] && baselineOn(a.type)));

  // Recurring cost of all add-ons after changes, per billing period
  const recurringTotal = computed((): number => {
    const integrations = toggleAddOns.value.filter((a) => !isIncluded(a) && selected.value[a.type]).reduce((sum, a) => sum + a.monthly, 0);
    const localization = localizationIncluded.value ? 0 : languages.value * localizationPrice.value;
    return (integrations + localization) * periodMultiplier.value;
  });

  const hasChanges = computed((): boolean => {
    if (codeBlocks.value > 0) return true;
    if (languages.value !== scheduledLanguages.value) return true;
    return toggleAddOns.value.some((a) => !isIncluded(a) && !!selected.value[a.type] !== baselineOn(a.type));
  });

  // Only these cost money today; everything else is free or takes effect at renewal
  const addsCharges = computed((): boolean => codeBlocks.value > 0 || languages.value > liveLanguages.value || newlyEnabled.value.length > 0);

  function changePayload() {
    return {
      additionalCodeBlocks: codeBlocks.value,
      localizationLanguages: languages.value,
      enable: [...newlyEnabled.value, ...restored.value].map((a) => a.type),
      disable: disabled.value.map((a) => a.type),
    };
  }

  // Ask the backend for the exact amount whenever a charge-affecting value changes (debounced)
  watch(
    [codeBlocks, languages, selected],
    () => {
      if (previewTimer) clearTimeout(previewTimer);
      if (!addsCharges.value) {
        previewing.value = false;
        dueToday.value = 0;
        return;
      }
      previewing.value = true;
      previewTimer = setTimeout(async () => {
        const response = await previewAddOns(changePayload());
        dueToday.value = response.status === "success" ? response.dueToday : 0;
        previewing.value = false;
      }, 400);
    },
    { deep: true }
  );

  onMounted(() => {
    toggleAddOns.value.forEach((a) => {
      selected.value[a.type] = baselineOn(a.type);
    });
    languages.value = scheduledLanguages.value;
  });

  function currency(value: number) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  }

  function formatLongDate(value?: string | null) {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  function updateCodeBlocks(direction: string) {
    if (direction === "inc" && codeBlocks.value < 100) {
      codeBlocks.value++;
    }
    if (direction === "dec" && codeBlocks.value > 0) {
      codeBlocks.value--;
    }
  }

  function updateLanguages(direction: string) {
    if (direction === "inc" && languages.value < 50) {
      languages.value++;
    }
    if (direction === "dec" && languages.value > 0) {
      languages.value--;
    }
  }

  function summary() {
    emitter.emit("billing-summary");
  }

  async function confirmChanges() {
    disableSubmit.value = true;

    const response = await updateAddOns(changePayload());

    if (response.status !== "success") {
      disableSubmit.value = false;
      toast.error(response.reason === "payment" ? t("billing_add_ons.payment_failed") : t("billing_add_ons.update_failed"));
      return;
    }

    confirmButtonRef.value!.innerHTML = confirmedButtonHTML;
    confirmButton.value = "btn-success";
    toast.success(
      response.effectiveDate ? t("billing_add_ons.updated_with_removals", { date: formatLongDate(response.effectiveDate) }) : t("billing_add_ons.updated")
    );
    setTimeout(() => emitter.emit("billing-summary", "refresh"), 1000);
  }
</script>

<style scoped>
  .spinner {
    padding: 0;
    width: 6.5rem;
    height: 32px;
    text-align: center;
    overflow: hidden;
    background-image: none;
  }

  .spinner-button .bi {
    font-size: 1.25em;
  }

  .spinner-button,
  .spinner-button:active .spinner-button:focus {
    outline: 0 !important;
    box-shadow: none !important;
  }

  .current {
    border-bottom: 1px solid lightgray;
  }

  .item-title {
    font-family: "proximanova-bold", sans-serif;
    font-size: 15px;
  }

  .item-detail {
    font-size: 14px;
  }

  .total-line {
    font-size: 16px;
    line-height: 1.6;
  }

  .charge-notice {
    font-size: 13px;
    color: #495057;
  }

  .btn.btn-success {
    transition-duration: 0.75s;
    animation-name: colorTransition;
  }

  @keyframes colorTransition {
    from {
      background-color: #3679e8 !important;
    }

    to {
      background-color: #38c172 !important;
    }
  }
</style>
